/*
 * The floating button of the shop chat (templates/_partials/chat.widget.html.twig) and what goes with it, without the
 * chat itself: app.js starts this on every page, the window (assets/shop-chat.js) loads on the first click only. The
 * page is the same for everybody (it is cached), so everything of the visitor happens here, in the browser:
 *
 * - Whether the chat shows at all: the button and every entry of the pages stand hidden until the shop has said that
 *   SES can answer now (ChatController::status; without credit, at its daily budget or switched off it cannot - user
 *   24.10.2026). The answer counts a minute, for the next pages of the session as well; a test shows itself to
 *   testers only (the cookie of ?chat_test=1).
 * - The conversation that goes on: its ID stays in localStorage for a day. While there is one for the market of the
 *   page, the button carries a dot and says "Gespräch fortsetzen"; an answer that came while the window was closed
 *   makes the dot pulse until the window opens again.
 * - On the product page the button steps aside while the buy box is in view: the box has its own entry, and the button
 *   would cover its right edge (the Klarna button on phones, the chosen size on narrow desktops).
 * - On product and basket pages a teaser beside the button points to the chat, once per session and only when the
 *   visitor hesitates: 30 seconds on the page or three changes of size or variant. It waits while the cookie notice,
 *   the window, the mini basket or the buy box is in the way, and it opens the window on a click only. Shown, click and
 *   dismiss go to the data layer (track()); whether they go on to GA4 is up to GTM and the consent of the visitor.
 * - The AI advisor's help after a failed payment (user 02.11.2026, chat.payment_help.html.twig): pages that show the
 *   visitor's basket carry it, the failure page and a failed wallet get it from the shop (ChatController::paymentHelp) -
 *   in the place the page has for it, else below the red note of the wallet button. x hides it for the session until
 *   the next failed attempt. It shows whether or not SES can answer now: the message is the shop's; its button to the
 *   chat waits for the answer of the status like every entry.
 */

var STORAGE_KEY = 'md_chat';
var STORAGE_TTL = 24 * 60 * 60 * 1000;
var STATUS_KEY = 'md_chat_status';
var STATUS_TTL = 60 * 1000;
var TEST_COOKIE = /(?:^|;\s*)md_chat_test=1(?:;|$)/;
var TEASER_KEY = 'md_chat_teaser';
var CHANGES_KEY = 'md_chat_changes';
var TEASER_SECONDS = 30;
var TEASER_CHANGES = 3;
// A size, length, colour or other variant of the buy box, a row of the size table: each opens another product page.
var CHANGE = 'a.product-page-chip[href]:not([aria-current]), [data-product-page-size-rows] .product-page-size-row';
var HELP_CLOSED_KEY = 'md_chat_help_closed';
var HELP_SEEN_KEY = 'md_chat_help_seen';

var launcher = null;
var label = '';
// Whether SES can answer now, once the shop has said so: the chat buttons of helps that come later show only then.
var available = false;
// Whether the shop has said it cannot: then a help shows its whole advice instead of the offer to help.
var unavailable = false;
// The conversation when the browser keeps nothing (private mode, storage blocked): it lasts as long as the page.
var memory = null;

export function start() {
    var chat = document.getElementById('shop-chat');
    // Off, or a test the visitor is not part of.
    if (!chat || (chat.getAttribute('data-chat-mode') === 'test' && !TEST_COOKIE.test(document.cookie))) return;

    helps(chat);
    status(chat.getAttribute('data-status-uri'), function (answer) {
        if (!answer) {
            advice(true);
            return;
        }
        available = true;
        document.querySelectorAll('[data-chat-entry]').forEach(function (entry) { entry.hidden = false; });
        fallbacks(true);
        launch();
    });
}

/**
 * SES stopped answering while the window was open (no credit, its daily budget): the window keeps its note with the
 * ways to the team, the button and the entries go - for this page and the next ones of the minute.
 */
export function pause() {
    available = false;
    keepStatus(false);
    document.querySelectorAll('[data-chat-entry]').forEach(function (entry) { entry.hidden = true; });
    fallbacks(false);
    advice(true);
}

/**
 * The problem report stands in for the chat where it is live (checkout.report_problem.html.twig, user 03.11.2026): it
 * goes once SES can answer and comes back when SES stops answering - without this script it simply stays.
 */
function fallbacks(hide) {
    document.querySelectorAll('[data-chat-fallback]').forEach(function (fallback) { fallback.hidden = hide; });
}

/**
 * While the advisor cannot answer, a help gives its whole advice instead of the reason and the offer to help - where
 * its card has one (a page Akamai keeps from before has the card of one message).
 */
function advice(on) {
    unavailable = on;
    document.querySelectorAll('[data-chat-help]').forEach(function (card) { adviceOf(card, on); });
}

function adviceOf(card, on) {
    var fallback = card.querySelector('[data-chat-help-fallback]');
    var text = card.querySelector('[data-chat-help-text]');
    if (!fallback || !fallback.textContent.trim()) return;
    fallback.hidden = !on;
    if (text) text.hidden = on;
}

/**
 * The help after a failed payment: the ones the page carries, the one of the failure page, the one of a wallet that
 * fails on this page (stripe-express.js says so with express:failed, once the shop has the failed attempt).
 */
function helps(chat) {
    var uri = chat.getAttribute('data-help-uri');
    var page = chat.getAttribute('data-page-type') || 'other';

    document.querySelectorAll('[data-chat-help]').forEach(function (help) {
        if (help.getAttribute('data-chat-help')) seen(help, page);
    });
    document.addEventListener('click', function (event) {
        var close = event.target instanceof Element ? event.target.closest('[data-chat-help-close]') : null;
        if (close) dismiss(close.closest('[data-chat-help]'), page);
    });

    var slot = document.querySelector('[data-chat-help-fetch]');
    if (slot) fetchHelp(uri, null, function (help) { place(help, [slot], page); });

    document.addEventListener('express:failed', function (event) {
        var wallet = event.target instanceof Element ? event.target : null;
        // the places of the page (checkout, basket page, status page of an order), else below the note of the button
        var slots = Array.prototype.slice.call(document.querySelectorAll('[data-chat-help-slot]'));
        var note = wallet && wallet.nextElementSibling && wallet.nextElementSibling.hasAttribute('data-express-message') ? wallet.nextElementSibling : null;
        if (!slots.length && !note) return;
        var order = slots.length ? slots[0].getAttribute('data-chat-help-order') : null;
        fetchHelp(uri, order, function (help) {
            if (slots.length) {
                place(help, slots, page);
            } else if (help) {
                place(help, [note], page, true);
            }
        });
    });
}

function fetchHelp(uri, order, then) {
    if (!uri || typeof window.fetch !== 'function') return;

    window.fetch(uri + (order ? '&order=' + encodeURIComponent(order) : ''), { credentials: 'same-origin', headers: { 'Accept': 'application/json', 'X-Requested-With': 'XMLHttpRequest' } })
        .then(function (response) { return response.ok ? response.json() : null; })
        .catch(function () { return null; })
        .then(function (answer) {
            var help = answer && answer.help && typeof answer.help.id === 'string' && typeof answer.help.text === 'string' ? answer.help : null;
            then(help);
        });
}

/**
 * The help in the places given - each replaced by it, or after the note of the wallet button ($after). A help closed
 * with x stays away; with none, the places stay as they are.
 */
function place(help, places, page, after) {
    var pattern = document.querySelector('template[data-chat-help-template]');
    if (!help || !pattern || closed() === help.id) return;

    places.forEach(function (where) {
        // the same help again, after a second failure of the same attempt: in place already
        if (where.getAttribute('data-chat-help') === help.id) return;
        var card = pattern.content.firstElementChild.cloneNode(true);
        card.setAttribute('data-chat-help', help.id);
        // the reason, the offer and the whole advice - or, in a pattern Akamai keeps from before, the one message
        var question = card.querySelector('[data-chat-help-question]');
        card.querySelector('[data-chat-help-text]').textContent = question && help.short ? help.short : help.text;
        if (question) question.textContent = help.question || '';
        var fallback = card.querySelector('[data-chat-help-fallback]');
        if (fallback) fallback.textContent = help.fallback || '';
        var open = card.querySelector('[data-chat-open]');
        open.setAttribute('data-chat-opener', help.id);
        (card.querySelector('[data-chat-help-ask]') || open).hidden = !available;
        adviceOf(card, unavailable);
        if (where.hasAttribute('data-chat-help-order')) card.setAttribute('data-chat-help-order', where.getAttribute('data-chat-help-order'));
        if (where.hasAttribute('data-chat-help-top')) card.setAttribute('data-chat-help-top', '');
        if (where.className && !after) card.className += ' ' + where.className.split(' ').filter(function (name) { return name.indexOf('checkout-support-') === 0 || name === 'shop-chat-assist-top'; }).join(' ');
        if (after) {
            var next = where.nextElementSibling;
            if (next && next.hasAttribute('data-chat-help')) next.remove();
            where.parentNode.insertBefore(card, where.nextSibling);
        } else {
            where.replaceWith(card);
        }
    });
    document.querySelectorAll('[data-chat-help="' + help.id + '"]').forEach(function (card) { seen(card, page); });
}

/**
 * Shown: once per failed attempt and session for the data layer.
 */
function seen(help, page) {
    var id = help.getAttribute('data-chat-help');
    if (help.hidden || closed() === id) return;
    try {
        if (window.sessionStorage.getItem(HELP_SEEN_KEY) === id) return;
        window.sessionStorage.setItem(HELP_SEEN_KEY, id);
    } catch (e) { /* counted on every page */ }
    track('chat_help_shown', { chat_teaser_page: page });
}

/**
 * x: the help of this failed attempt goes, on this page and the next ones of the session - the next failed attempt
 * has a help of its own.
 */
function dismiss(help, page) {
    if (!help) return;
    var id = help.getAttribute('data-chat-help');
    document.querySelectorAll('[data-chat-help="' + id + '"]').forEach(function (card) { card.hidden = true; });
    try {
        window.sessionStorage.setItem(HELP_CLOSED_KEY, id);
    } catch (e) { /* this page only */ }
    track('chat_help_dismiss', { chat_teaser_page: page });
}

function closed() {
    try {
        return window.sessionStorage.getItem(HELP_CLOSED_KEY);
    } catch (e) {
        return null;
    }
}

/**
 * Whether the chat can answer: the answer of the last minute, or the shop's (ChatController::status). The minute is
 * part of the address, so no copy at the edge outlives it; asked without cookies, so the shop starts no session.
 * No answer is no chat.
 */
function status(uri, then) {
    var known = knownStatus();
    if (known !== null) {
        then(known);
        return;
    }
    if (!uri || typeof window.fetch !== 'function') {
        then(false);
        return;
    }

    window.fetch(uri + '&m=' + Math.floor(Date.now() / STATUS_TTL), { credentials: 'omit', headers: { 'Accept': 'application/json' } })
        .then(function (response) { return response.ok ? response.json() : null; })
        .catch(function () { return null; })
        .then(function (answer) {
            var available = !!answer && answer.available === true;
            keepStatus(available);
            then(available);
        });
}

function knownStatus() {
    try {
        var known = JSON.parse(window.sessionStorage.getItem(STATUS_KEY) || 'null');
        return known && typeof known.available === 'boolean' && Date.now() - known.at < STATUS_TTL && Date.now() >= known.at ? known.available : null;
    } catch (e) {
        return null;
    }
}

function keepStatus(available) {
    try {
        window.sessionStorage.setItem(STATUS_KEY, JSON.stringify({ available: available, at: Date.now() }));
    } catch (e) { /* every page asks again */ }
}

/**
 * The button, once the chat can answer: its dot, its place beside the buy box, its teaser.
 */
function launch() {
    var dock = document.querySelector('[data-chat-dock]');
    // A page of the purchase has no button.
    if (!dock) return;

    launcher = dock.querySelector('[data-chat-launcher]');
    label = launcher.getAttribute('aria-label') || '';
    refresh();
    // Another tab of the shop, or the page coming back from the back-forward cache.
    window.addEventListener('storage', function (event) {
        if (event.key === STORAGE_KEY || event.key === null) refresh();
    });
    window.addEventListener('pageshow', function (event) {
        if (event.persisted) refresh();
    });

    watchBuyBox(dock);
    tease(dock);
}

/**
 * The conversation of the market of the page that goes on, or null: { id, locale, expires, unread }.
 */
export function conversation() {
    if (memory) return memory;
    try {
        var stored = JSON.parse(window.localStorage.getItem(STORAGE_KEY) || 'null');
        return stored && typeof stored.id === 'string' && stored.expires > Date.now() && stored.locale === locale() ? stored : null;
    } catch (e) {
        return null;
    }
}

/**
 * Keeps the conversation for a day from its last answer: unread when that came while the window was closed.
 */
export function remember(id, unread) {
    save({ id: id, locale: locale(), expires: Date.now() + STORAGE_TTL, unread: !!unread });
}

/**
 * The window shows the conversation: its answers are read.
 */
export function read() {
    var current = conversation();
    if (current && current.unread) {
        current.unread = false;
        save(current);
    }
}

export function forget() {
    memory = null;
    try {
        window.localStorage.removeItem(STORAGE_KEY);
    } catch (e) { /* nothing stored */ }
    refresh();
}

function save(entry) {
    memory = null;
    try {
        window.localStorage.setItem(STORAGE_KEY, JSON.stringify(entry));
    } catch (e) {
        memory = entry;
    }
    refresh();
}

/**
 * A page of another market or language (/nl-nl/ on the same host) has a conversation of its own.
 */
function locale() {
    var chat = document.getElementById('shop-chat');
    return chat ? chat.getAttribute('data-locale') : null;
}

function refresh() {
    if (!launcher) return;

    var current = conversation();
    var text = current ? launcher.getAttribute('data-label-continue') || label : label;
    if (current) {
        launcher.setAttribute('data-chat-state', current.unread ? 'unread' : 'running');
    } else {
        launcher.removeAttribute('data-chat-state');
    }
    launcher.setAttribute('aria-label', text);
    launcher.setAttribute('title', text);
}

/**
 * The button steps aside while any part of the buy box of the product page is in view.
 */
function watchBuyBox(dock) {
    var entry = document.querySelector('.product-page-box [data-chat-entry]');
    var box = entry ? entry.closest('.product-page-box') : null;
    if (!box || typeof IntersectionObserver !== 'function') return;

    new IntersectionObserver(function (entries) {
        dock.classList.toggle('shop-chat-dock-away', entries[entries.length - 1].isIntersecting);
    }).observe(box);
}

function tease(dock) {
    var teaser = dock.querySelector('[data-chat-teaser]');
    // Once per session: without a session to remember it in, never. Not for a visitor who already talks to the chat.
    if (!teaser || teased() || conversation()) return;

    var page = teaser.getAttribute('data-chat-teaser');
    var seconds = 0;
    var shown = false;
    var timer = null;

    function finish(state) {
        clearInterval(timer);
        document.removeEventListener('click', clicked);
        teaser.hidden = true;
        if (state) keep(TEASER_KEY, state);
    }

    // Opening the chat ends the teaser for the session; a change of size or variant counts towards it.
    function clicked(event) {
        var target = event.target instanceof Element ? event.target : null;
        if (!target) return;
        if (target.closest('[data-chat-open]')) {
            finish('opened');
            return;
        }
        if (shown || event.button > 0 || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;
        if (target.closest(CHANGE)) keep(CHANGES_KEY, String(changes() + 1));
    }

    teaser.querySelector('[data-chat-teaser-open]').addEventListener('click', function () {
        track('chat_teaser_click', { chat_teaser_page: page });
        finish(null);
        launcher.click();
    });
    teaser.querySelector('[data-chat-teaser-close]').addEventListener('click', function () {
        track('chat_teaser_dismiss', { chat_teaser_page: page });
        finish('dismissed');
    });
    document.addEventListener('click', clicked);

    timer = setInterval(function () {
        if (document.visibilityState !== 'hidden') seconds++;
        var free = !dock.hidden
            && !dock.classList.contains('shop-chat-dock-away')
            && !document.documentElement.classList.contains('shop-chat-open')
            && !document.querySelector('.offcanvas.show, .modal.show')
            && !consentPending();
        if (shown) {
            teaser.hidden = !free;
            return;
        }
        if (!free || (seconds < TEASER_SECONDS && changes() < TEASER_CHANGES)) return;
        if (!keep(TEASER_KEY, 'shown')) {
            finish(null);
            return;
        }
        shown = true;
        teaser.hidden = false;
        track('chat_teaser_shown', { chat_teaser_page: page });
    }, 1000);
}

/**
 * The cookie notice is open, or still to come: it shows a moment after the page while the visitor has not decided.
 */
function consentPending() {
    var notice = document.getElementById('md_cookie_allow-undefined');
    return !/(?:^|;\s*)md_cookie_allow=/.test(document.cookie) || (!!notice && !notice.classList.contains('d-none'));
}

function teased() {
    try {
        return window.sessionStorage.getItem(TEASER_KEY) !== null;
    } catch (e) {
        return true;
    }
}

function changes() {
    try {
        return parseInt(window.sessionStorage.getItem(CHANGES_KEY), 10) || 0;
    } catch (e) {
        return 0;
    }
}

function keep(key, value) {
    try {
        window.sessionStorage.setItem(key, value);
        return true;
    } catch (e) {
        return false;
    }
}

/**
 * An event of the chat for the data layer, also for shop-chat.js; whether it goes on to GA4 is up to GTM and the consent
 * of the visitor.
 */
export function track(event, data) {
    window.dataLayer = window.dataLayer || [];
    window.dataLayer.push(Object.assign({ event: event }, data || {}));
}
